/**
 * EmojiPicker — popover-сетка популярных эмодзи без внешних зависимостей.
 *
 * Используется в ChatComposer (RichTextEditor toolbar) — клик по 😊 показывает
 * грид, клик по эмодзи зовёт onSelect(char). Категории как в Telegram:
 * смайлы, жесты, сердца, символы, флаги (минимальный набор).
 *
 * Почему не npm package: emoji-picker-react ~400KB gzip + own theming +
 * native font fallback на разных OS даёт inconsistent visuals. Своя сетка
 * легче (1 KB), рендерится через системный emoji font, всегда выглядит
 * как в TG/iOS/Win11. Если потребуется поиск/категории/реакции —
 * переключимся на пакет.
 */
import { useState } from "react";
import { Button, Popover, Tabs, Tooltip } from "antd";

interface Props {
  onSelect: (emoji: string) => void;
}

// Категории. Каждая ~24-32 emoji — самые употребимые.
const CATEGORIES: { key: string; title: string; emojis: string[] }[] = [
  {
    key: "smileys",
    title: "😊",
    emojis: [
      "😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","😉","😊",
      "😇","🥰","😍","🤩","😘","😗","☺️","😚","😙","🥲","😋","😛",
      "😜","🤪","😝","🤑","🤗","🤭","🫢","🤫","🤔","🫡","🤐","🤨",
      "😐","😑","😶","🫥","😏","😒","🙄","😬","😮‍💨","🤥","😌","😔",
      "😪","🤤","😴","😷","🤒","🤕","🤢","🤮","🤧","🥵","🥶","🥴",
      "😵","🤯","🤠","🥳","🥸","😎","🤓","🧐","😕","🫤","😟","🙁",
      "☹️","😮","😯","😲","😳","🥺","🥹","😦","😧","😨","😰","😥",
      "😢","😭","😱","😖","😣","😞","😓","😩","😫","🥱","😤","😡",
    ],
  },
  {
    key: "gestures",
    title: "👍",
    emojis: [
      "👍","👎","👌","✌️","🤞","🫰","🤟","🤘","🤙","👈","👉","👆",
      "🖕","👇","☝️","🫵","✋","🤚","🖐️","🖖","👋","🤛","🤜","✊",
      "👊","🫳","🫴","🫲","🫱","👏","🙌","🫶","👐","🤲","🤝","🙏",
      "💪","🦾","👀","👁️","🧠","🫀","🫁","🦷","🦴","👃","👂","🦻",
    ],
  },
  {
    key: "hearts",
    title: "❤️",
    emojis: [
      "❤️","🧡","💛","💚","💙","💜","🤎","🖤","🤍","♥️","💔","❣️",
      "💕","💞","💓","💗","💖","💘","💝","💟","💌","💋","💯","💫",
      "⭐","🌟","✨","🔥","💥","☀️","🌈","🌹","🌸","🌺","🌷","🌻",
    ],
  },
  {
    key: "objects",
    title: "🎉",
    emojis: [
      "🎉","🎊","🎁","🎂","🍰","🍕","🍔","🍟","🍿","🍫","🍩","🍪",
      "🍎","🍌","🍓","🍒","🥑","🌮","🍣","🍜","☕","🍺","🍷","🥂",
      "🍾","💧","🌊","☔","❄️","⛄","🎈","🎀","💎","👑","💄","👜",
      "📱","💻","⌚","📷","📺","🎧","🎮","🎲","⚽","🏀","🎯","🏆",
    ],
  },
  {
    key: "symbols",
    title: "✅",
    emojis: [
      "✅","❌","⚠️","🚫","⛔","❓","❗","‼️","⁉️","💢","🆗","🆕",
      "🆒","🆓","🔝","🔚","🔙","🔜","🔛","🔄","🔁","🔂","▶️","⏸️",
      "⏹️","⏺️","⏭️","⏮️","⏩","⏪","🔀","🔼","🔽","⬆️","⬇️","➡️",
      "⬅️","🔔","🔕","💬","💭","🗯️","♻️","✔️","✖️","➕","➖","➗",
    ],
  },
];


export const EmojiPicker = ({ onSelect }: Props) => {
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(CATEGORIES[0].key);

  const content = (
    <div style={{ width: 320 }}>
      <Tabs
        size="small"
        activeKey={activeKey}
        onChange={setActiveKey}
        items={CATEGORIES.map((c) => ({
          key: c.key,
          label: <span style={{ fontSize: 16 }}>{c.title}</span>,
          children: (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(8, 1fr)",
              gap: 2,
              maxHeight: 240,
              overflowY: "auto",
              padding: "4px 2px",
            }}>
              {c.emojis.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => onSelect(e)}
                  style={{
                    fontSize: 22,
                    lineHeight: 1,
                    width: 34,
                    height: 34,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    borderRadius: 4,
                    padding: 0,
                  }}
                  onMouseEnter={(ev) => {
                    (ev.currentTarget as HTMLElement).style.background = "#f0f0f0";
                  }}
                  onMouseLeave={(ev) => {
                    (ev.currentTarget as HTMLElement).style.background = "transparent";
                  }}
                  title={e}
                >
                  {e}
                </button>
              ))}
            </div>
          ),
        }))}
        tabBarStyle={{ marginBottom: 4 }}
      />
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="topLeft"
      destroyOnHidden
    >
      <Tooltip title="Эмодзи">
        <Button
          size="small"
          type="text"
          style={{ fontSize: 16, padding: "0 6px", height: 28, lineHeight: 1 }}
        >
          😊
        </Button>
      </Tooltip>
    </Popover>
  );
};
